import { useEffect, useRef, useState } from 'react';

interface PulseOdometerProps {
  value: number;
  /** Format with commas. Default true. */
  commas?: boolean;
}

/**
 * Animated odometer-style number counter.
 * Digits roll up/down when the value changes, creating a "mission control" feel.
 */
export function PulseOdometer({ value, commas = true }: PulseOdometerProps) {
  const formatted = commas ? value.toLocaleString('en-AU') : String(value);
  const prevRef = useRef(formatted);
  const [digits, setDigits] = useState<DigitState[]>(initDigits(formatted));

  useEffect(() => {
    const prev = prevRef.current;
    prevRef.current = formatted;

    if (prev === formatted) return;

    // Generate new digit states with animation direction.
    const prevChars = prev.split('');
    const newChars = formatted.split('');
    const maxLen = Math.max(prevChars.length, newChars.length);
    const nextDigits: DigitState[] = [];

    for (let i = 0; i < maxLen; i++) {
      const prevIdx = i - (maxLen - prevChars.length);
      const newIdx = i - (maxLen - newChars.length);
      const prevChar = prevIdx >= 0 ? (prevChars[prevIdx] ?? '') : '';
      const newChar = newIdx >= 0 ? (newChars[newIdx] ?? '') : '';

      nextDigits.push({
        char: newChar,
        animate: prevChar !== newChar,
        direction: (Number(newChar) || 0) > (Number(prevChar) || 0) ? 'up' : 'down',
      });
    }

    setDigits(nextDigits);
  }, [formatted]);

  return (
    <span className="pulse-counter inline-flex" aria-label={String(value)}>
      {digits.map((d, i) => (
        <span
          key={`${i}-${d.char}`}
          className={`inline-block ${d.animate ? (d.direction === 'up' ? 'pulse-digit-roll-up' : 'pulse-digit-roll-down') : ''}`}
        >
          {d.char}
        </span>
      ))}
    </span>
  );
}

interface DigitState {
  char: string;
  animate: boolean;
  direction: 'up' | 'down';
}

function initDigits(formatted: string): DigitState[] {
  return formatted.split('').map((char) => ({ char, animate: false, direction: 'up' as const }));
}
